import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { type NodeType } from '@atlaskit/editor-prosemirror/model';
import { type Selection } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../ui/utils/analytics/analyticsFlowTypes';
import type { Positions } from '../../ui/utils/xstate/get-ai-experience-service';

export interface EndExperienceAction {
	data: { ignoredStartTriggerSelection?: Selection; modalDecorationSet?: DecorationSet };
	type: 'END_EXPERIENCE';
}
export interface ShowModalAction {
	data: {
		configItem: EditorPluginAIConfigItem;
		hideLoadingScreen?: boolean;
		initialPositions: Positions;
		initialPrompt?: string;
		initialSelection?: Selection;
		interactionSessionId?: string;
		lastTriggeredFrom?: InvokedFrom;
		modalDecorationSet: DecorationSet;
		modalMountedTimeStamp: number;
		nudgeMetrics?: Record<string, number | undefined>;
		triggeredFor?: NodeType;
		triggerMethod?: InvokedFromTriggerMethod;
		withAcceptReject?: boolean;
		withStreamingHighlight?: boolean;
	};
	type: 'SHOW_MODAL';
}

export interface UpdateModalDecorationSetAction {
	data: {
		modalDecorationSet: DecorationSet;
	};
	type: 'UPDATE_MODAL_DECORATION_SET';
}
export interface SetExperienceSessionAction {
	data: {
		sessionId: string;
	};
	type: 'START_SESSION' | 'END_SESSION';
}
export interface UpdateTargetIdAction {
	data: {
		targetId: string | number;
	};
	type: 'UPDATE_TARGET_ID';
}

/**
 * Calculates positions from modalDecorationSet and initialPositions.
 * This logic mirrors the calculation done in ContentComponent.
 */
function calculatePositions(
	modalDecorationSet: DecorationSet,
	initialPositions?: Positions,
): Positions | undefined {
	// Prioritise getting position from inlineDecoration,
	// otherwise get decoration position from start or end widget
	let positions: [number, number] = [0, 0];
	const decoration = modalDecorationSet
		.find(
			undefined,
			undefined,
			(spec) =>
				spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
		)
		.shift();

	if (decoration) {
		// when there is decoration, it means it's text selection
		// we need to use the initial positions
		// as the decoration positions are not accurate when there the selection is empty
		if (!initialPositions) {
			return undefined;
		}
		positions = initialPositions;
	} else {
		const nodeDecorations = modalDecorationSet.find(
			undefined,
			undefined,
			(spec) => spec.key === 'ai-nodeDecoration',
		);
		if (nodeDecorations.length > 0) {
			const startPos = Math.min(...nodeDecorations.map((decoration) => decoration.from));
			const endPos = Math.max(...nodeDecorations.map((decoration) => decoration.to));
			positions = [startPos, endPos];
		} else {
			return undefined;
		}
	}

	return positions;
}

export interface AIDecorationExperiencePluginState {
	configItem?: EditorPluginAIConfigItem;
	/**
	 * This is the current session id which the active AI experiecne is using.
	 */
	currentSessionId?: string;
	dispatchAnalyticsEvent: PMPluginFactoryParams['dispatchAnalyticsEvent'];

	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/**
	 * This stores a selection which will be used to determine if the space shortcut which starts an experience should be ignored.
	 */
	ignoredSpaceTriggerSelection?: Selection;
	initialPositions?: Positions;
	initialPrompt?: string;
	/**
	 * this is used to restore the selection when the in-document streaming is discarded
	 */
	initialSelection?: Selection;
	/**
	 * This is a unique id representing the current interaction. An interaction is from the moment the experience is invoked
	 * through to when the experience is ended. This id will remain set even past the end of an experience and will only update
	 * once when the experience is started.
	 */
	interactionSessionId?: string;
	isInDocumentStreamingEnabled?: boolean;
	isRunning: boolean;
	/**
	 * Explicitly tracks when the AcceptRejectScreenWithLogic is being shown to the user
	 */
	isShowingAcceptRejectScreen?: boolean;
	lastTriggeredFrom?: InvokedFrom;
	modalDecorationSet: DecorationSet;
	modalMountedTimeStamp?: number;
	/**
	 * This is the session id which will be used when the next AI experience is started.
	 */
	nextSessionId?: string;
	/**
	 * Nudge metrics will be used passed in analytics events.
	 * It includes detailed metrics for improve writing nudge.
	 */
	nudgeMetrics?: Record<string, number | undefined>;
	/**
	 * Calculated positions for the modal based on decorations and initial positions.
	 * This is computed by the reducer when decorations change.
	 */
	positions?: Positions;
	/**
	 * If the session has ended
	 */
	sessionEnded?: boolean;
	/**
	 * If the session has started
	 */
	sessionStarted?: boolean;
	/**
	 * The id of the AIFC content target, which will be supplied to backend APIs,
	 * and yielded in every chunk of network stream events. This is used to determine
	 * which content target to use for content mutations.
	 */
	targetId?: string | number;

	triggeredFor?: NodeType;

	triggerMethod?: InvokedFromTriggerMethod;
	withAcceptReject?: boolean;

	withStreamingHighlight?: boolean;
}

export function reducer(
	state: AIDecorationExperiencePluginState,
	action:
		| EndExperienceAction
		| ShowModalAction
		| UpdateModalDecorationSetAction
		| SetExperienceSessionAction
		| UpdateTargetIdAction,
): AIDecorationExperiencePluginState {
	switch (action.type) {
		case 'END_EXPERIENCE':
			// When the experience ends the plugin state needs to reset everything back to the initial plugin state
			return {
				modalDecorationSet: DecorationSet.empty,
				dispatchAnalyticsEvent: state.dispatchAnalyticsEvent,
				initialPositions: undefined,
				initialSelection: undefined,
				positions: undefined,
				...action.data,
				isRunning: false,
				isShowingAcceptRejectScreen: false,
				targetId: state.targetId,
				currentSessionId:
					state.currentSessionId === state.nextSessionId ? state.currentSessionId : undefined,
				nextSessionId: state.nextSessionId,
				hideLoadingScreen: fg('platform_editor_ai_aifc_patch_ga')
					? state.nextSessionId
						? state.hideLoadingScreen
						: false
					: undefined,
				sessionEnded: false,
				isInDocumentStreamingEnabled: state.isInDocumentStreamingEnabled,
			};
		case 'SHOW_MODAL':
			return {
				...state,
				...action.data,
				isRunning: true,
				isShowingAcceptRejectScreen: action.data.withAcceptReject && !state.nextSessionId,
				currentSessionId: state.nextSessionId,
				// if somehow END_EXPERIENCE is called before END_SESSION, reset sessionEnded to false here
				sessionEnded: false,
				positions: calculatePositions(action.data.modalDecorationSet, action.data.initialPositions),
				ignoredSpaceTriggerSelection: undefined,
			};
		case 'UPDATE_MODAL_DECORATION_SET':
			return {
				...state,
				modalDecorationSet: action.data.modalDecorationSet,
				positions: calculatePositions(action.data.modalDecorationSet, state.initialPositions),
			};
		case 'START_SESSION':
			return {
				...state,
				nextSessionId: action.data.sessionId,
				sessionStarted: true,
				sessionEnded: false,
				isShowingAcceptRejectScreen: false,
			};
		case 'END_SESSION':
			return {
				...state,
				nextSessionId: action.data.sessionId,
				currentSessionId: state.isRunning ? state.currentSessionId : action.data.sessionId,
				hideLoadingScreen: fg('platform_editor_ai_aifc_patch_ga') ? false : state.hideLoadingScreen,
				sessionStarted: false,
				sessionEnded: true,
			};
		case 'UPDATE_TARGET_ID':
			return {
				...state,
				targetId: action.data.targetId,
			};
		default:
			return state;
	}
}
